// Print Metadata Configuration Tab
// Sprint 3: Metadata Input UI

import React, { useState } from 'react';
import { Label } from '@/app/components/ui/label';
import { Input } from '@/app/components/ui/input';
import { Textarea } from '@/app/components/ui/textarea';
import { Button } from '@/app/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Calendar, User, Building2, FileText, Plus, Trash2, GripVertical } from 'lucide-react';
import type { PrintMetadata } from '@/app/types/print';
import { useLanguage } from '@/app/contexts/LanguageContext';

interface PrintMetadataTabProps {
  metadata: PrintMetadata;
  onUpdateMetadata: (updates: Partial<PrintMetadata>) => void;
}

export function PrintMetadataTab({ metadata, onUpdateMetadata }: PrintMetadataTabProps) {
  const { t } = useLanguage();
  const [newFieldKey, setNewFieldKey] = useState('');
  const [newFieldValue, setNewFieldValue] = useState('');
  
  const customFields = metadata.customFields || {};
  const customFieldEntries = Object.entries(customFields);
  
  const handleAddField = () => {
    const trimmedKey = newFieldKey.trim();
    const trimmedValue = newFieldValue.trim();
    
    if (!trimmedKey) {
      return; // Ignore empty keys
    }
    
    if (customFields[trimmedKey]) {
      // Key already exists - ignore or could show toast
      return;
    }
    
    onUpdateMetadata({
      customFields: {
        ...customFields,
        [trimmedKey]: trimmedValue,
      },
    });
    
    setNewFieldKey('');
    setNewFieldValue('');
  };
  
  const handleUpdateField = (oldKey: string, newKey: string, newValue: string) => {
    const trimmedKey = newKey.trim();
    
    if (!trimmedKey) {
      // If key becomes empty, delete the field
      handleDeleteField(oldKey);
      return;
    }
    
    const updatedFields = { ...customFields };
    
    // If key changed, remove old key
    if (oldKey !== trimmedKey) {
      delete updatedFields[oldKey];
      
      // Check if new key already exists
      if (customFields[trimmedKey]) {
        return; // Key collision - ignore
      }
    }
    
    updatedFields[trimmedKey] = newValue;
    
    onUpdateMetadata({
      customFields: updatedFields,
    });
  };
  
  const handleDeleteField = (key: string) => {
    const updatedFields = { ...customFields };
    delete updatedFields[key];
    
    onUpdateMetadata({
      customFields: updatedFields,
    });
  };
  
  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h3 className="text-lg font-medium mb-2">{t.table.printDialog.metadata.title}</h3>
        <p className="text-sm text-muted-foreground">
          {t.table.printDialog.metadata.subtitle}
        </p>
      </div>
      
      {/* Project Information */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <FileText className="w-4 h-4" />
            {t.table.printDialog.metadata.project.title}
          </CardTitle>
          <CardDescription>{t.table.printDialog.metadata.project.desc}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="projectName">{t.table.printDialog.metadata.project.name}</Label>
            <Input
              id="projectName"
              value={metadata.projectName}
              onChange={(e) => onUpdateMetadata({ projectName: e.target.value })}
              placeholder={t.table.printDialog.metadata.project.namePlaceholder}
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="projectDescription">{t.table.printDialog.metadata.project.description}</Label>
            <Textarea
              id="projectDescription"
              value={metadata.projectDescription || ''}
              onChange={(e) =>
                onUpdateMetadata({ projectDescription: e.target.value })
              }
              placeholder={t.table.printDialog.metadata.project.descPlaceholder}
              rows={3}
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="version">{t.table.printDialog.metadata.project.version}</Label>
            <Input
              id="version"
              value={metadata.version}
              onChange={(e) => onUpdateMetadata({ version: e.target.value })}
              placeholder="1.0"
            />
          </div>
        </CardContent>
      </Card>
      
      {/* Author & Company */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <User className="w-4 h-4" />
            {t.table.printDialog.metadata.author.title}
          </CardTitle>
          <CardDescription>{t.table.printDialog.metadata.author.desc}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="author">{t.table.printDialog.metadata.author.name}</Label>
            <Input
              id="author"
              value={metadata.author || ''}
              onChange={(e) => onUpdateMetadata({ author: e.target.value })}
              placeholder={t.table.printDialog.metadata.author.namePlaceholder}
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="companyName" className="flex items-center gap-2">
              <Building2 className="w-4 h-4" />
              {t.table.printDialog.metadata.author.company}
            </Label>
            <Input
              id="companyName"
              value={metadata.companyName || ''}
              onChange={(e) => onUpdateMetadata({ companyName: e.target.value })}
              placeholder={t.table.printDialog.metadata.author.companyPlaceholder}
            />
          </div>
        </CardContent>
      </Card>
      
      {/* Dates */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            {t.table.printDialog.metadata.dates.title}
          </CardTitle>
          <CardDescription>{t.table.printDialog.metadata.dates.desc}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="dateCreated">{t.table.printDialog.metadata.dates.created}</Label>
              <Input
                id="dateCreated"
                type="date"
                value={metadata.dateCreated.toISOString().split('T')[0]}
                onChange={(e) => {
                  const date = new Date(e.target.value);
                  if (!isNaN(date.getTime())) {
                    onUpdateMetadata({ dateCreated: date });
                  }
                }}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="dateModified">{t.table.printDialog.metadata.dates.modified}</Label>
              <Input
                id="dateModified"
                type="date"
                value={metadata.dateModified.toISOString().split('T')[0]}
                onChange={(e) => {
                  const date = new Date(e.target.value);
                  if (!isNaN(date.getTime())) {
                    onUpdateMetadata({ dateModified: date });
                  }
                }}
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="datePrinted">{t.table.printDialog.metadata.dates.printed}</Label>
            <Input
              id="datePrinted"
              type="text"
              value={metadata.datePrinted.toLocaleDateString('de-DE')}
              disabled
              className="bg-muted"
            />
            <p className="text-xs text-muted-foreground">
              {t.table.printDialog.metadata.dates.autoUpdate}
            </p>
          </div>
        </CardContent>
      </Card>
      
      {/* Custom Fields */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t.table.printDialog.metadata.custom.title}</CardTitle>
          <CardDescription>
            {t.table.printDialog.metadata.custom.desc}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Existing Fields */}
          {customFieldEntries.length > 0 && (
            <div className="space-y-3">
              {customFieldEntries.map(([key, value]) => (
                <div key={key} className="flex items-center gap-3">
                  <div className="flex-1 grid grid-cols-2 gap-3">
                    <Input
                      value={key}
                      onChange={(e) => handleUpdateField(key, e.target.value, value)}
                      placeholder={t.table.printDialog.metadata.custom.fieldName}
                      className="font-medium"
                    />
                    <Input
                      value={value}
                      onChange={(e) => handleUpdateField(key, key, e.target.value)}
                      placeholder={t.table.printDialog.metadata.custom.value}
                    />
                  </div>
                  <Button
                    onClick={() => handleDeleteField(key)}
                    variant="ghost"
                    size="icon"
                    className="flex-shrink-0"
                  >
                    <Trash2 className="w-4 h-4 text-destructive" />
                  </Button>
                </div>
              ))}
            </div>
          )}
          
          {/* Add New Field */}
          <div className="pt-2">
            <div className="grid grid-cols-2 gap-3 mb-3">
              <Input
                value={newFieldKey}
                onChange={(e) => setNewFieldKey(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && newFieldKey.trim() && newFieldValue.trim()) {
                    handleAddField();
                  }
                }}
                placeholder={t.table.printDialog.metadata.custom.fieldName}
              />
              <Input
                value={newFieldValue}
                onChange={(e) => setNewFieldValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && newFieldKey.trim() && newFieldValue.trim()) {
                    handleAddField();
                  }
                }}
                placeholder={t.table.printDialog.metadata.custom.value}
              />
            </div>
            <Button
              onClick={handleAddField}
              disabled={!newFieldKey.trim() || !newFieldValue.trim()}
              variant="outline"
              className="w-full"
            >
              <Plus className="w-4 h-4 mr-2" />
              {t.table.printDialog.metadata.custom.addBtn}
            </Button>
          </div>
          
          {customFieldEntries.length === 0 && (
            <div className="text-sm text-muted-foreground text-center py-6 border-2 border-dashed rounded-md">
              {t.table.printDialog.metadata.custom.empty}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}